import { useSpinDelay } from 'spin-delay'
import { cn } from '#app/utils/misc'
import { AnimatedButton, type AnimatedButtonProps } from './animated-button'
import { Icon } from './icon'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from './tooltip'

interface AnimatedStatusButtonProps extends AnimatedButtonProps {
	status: 'pending' | 'success' | 'error' | 'idle'
	message?: string | null
	spinDelay?: Parameters<typeof useSpinDelay>[1]
}

/**
 * AnimatedStatusButton - Combines AnimatedButton animations with StatusButton status functionality
 *
 * Features:
 * - All AnimatedButton animations (hover, press, entrance)
 * - Status indicators (pending, success, error, idle)
 * - Spinner delay for pending state
 * - Tooltip support for status messages
 */
export function AnimatedStatusButton({
	status,
	message,
	spinDelay,
	className,
	children,
	...props
}: AnimatedStatusButtonProps) {
	const delayedPending = useSpinDelay(status === 'pending', {
		delay: 400,
		minDuration: 300,
		...spinDelay,
	})

	const companion = {
		error: (
			<div className="inline-flex size-6 items-center justify-center rounded-full bg-destructive">
				<Icon
					className="text-destructive-foreground"
					name="cross-1"
					title="Error"
				/>
			</div>
		),
		idle: null,
		pending: delayedPending ? (
			<div className="inline-flex size-6 items-center justify-center">
				<Icon className="animate-spin" name="update" title="Loading" />
			</div>
		) : null,
		success: (
			<div className="inline-flex size-6 items-center justify-center">
				<Icon name="check" title="Success" />
			</div>
		),
	}[status]

	return (
		<AnimatedButton
			{...props}
			className={cn('flex justify-center gap-4', className)}
		>
			<div className="inline-flex items-center gap-2">{children}</div>
			{message ? (
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger>{companion}</TooltipTrigger>
						<TooltipContent>{message}</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			) : (
				companion
			)}
		</AnimatedButton>
	)
}
